import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  EmptyState,
  ErrorState,
  FieldLabel,
  LoadingState,
  Modal,
  PageHeading,
  StatusBadge,
} from "@/components/app/AppUI";
import { eventRepository, taskRepository } from "@/data/repositories";
import type { TaskStatus } from "@/domain/types";

export const Route = createFileRoute("/_authenticated/app/tareas")({ component: TasksPage });
type TaskForm = { eventId: string; title: string; dueAt: string; priority: string };
function TasksPage() {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: () => taskRepository.list() });
  const events = useQuery({ queryKey: ["events"], queryFn: () => eventRepository.list() });
  const invalidate = () => client.invalidateQueries({ queryKey: ["tasks"] });
  const status = useMutation({
    mutationFn: ({ id, value }: { id: string; value: TaskStatus }) =>
      taskRepository.setStatus(id, value),
    onSuccess: invalidate,
  });
  const save = useMutation({
    mutationFn: (values: TaskForm) =>
      taskRepository.save({
        eventId: values.eventId || null,
        title: values.title,
        dueAt: values.dueAt ? new Date(values.dueAt).toISOString() : null,
        priority: Number(values.priority),
      }),
    onSuccess: () => {
      toast.success("Tarea creada");
      setOpen(false);
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });
  if (tasks.isLoading || events.isLoading) return <LoadingState />;
  if (tasks.error || events.error)
    return <ErrorState message={tasks.error?.message ?? events.error?.message ?? "Error"} />;
  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Operaciones"
        title="Tareas"
        description="Pendientes accionables, con fecha y contexto de evento."
        action={
          <button className="btn-primary" onClick={() => setOpen(true)}>
            <Plus className="size-4" /> Nueva tarea
          </button>
        }
      />
      <section className="panel">
        {(tasks.data ?? []).length === 0 ? (
          <EmptyState title="Todo al día" description="No hay tareas pendientes." />
        ) : (
          tasks.data?.map((task) => (
            <div
              key={task.id}
              className={`data-row grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1.2fr_1fr_8rem_auto] ${task.status === "DONE" ? "opacity-55" : ""}`}
            >
              <button
                className={
                  task.status === "DONE" ? "btn-quiet !min-h-9 !px-3" : "btn-primary !min-h-9 !px-3"
                }
                onClick={() =>
                  status.mutate({ id: task.id, value: task.status === "DONE" ? "OPEN" : "DONE" })
                }
              >
                {task.status === "DONE" ? (
                  <RotateCcw className="size-4" />
                ) : (
                  <Check className="size-4" />
                )}
              </button>
              <div>
                <p className={`font-semibold ${task.status === "DONE" ? "line-through" : ""}`}>
                  {task.title}
                </p>
                <p className="text-sm text-muted-foreground">Prioridad {task.priority}</p>
              </div>
              <p className="hidden md:block">{task.events?.title ?? "General"}</p>
              <time className="hidden text-sm md:block">
                {task.due_at
                  ? new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short" }).format(
                      new Date(task.due_at),
                    )
                  : "Sin fecha"}
              </time>
              <StatusBadge status={task.status} />
            </div>
          ))
        )}
      </section>
      {open && (
        <TaskModal
          events={events.data ?? []}
          onClose={() => setOpen(false)}
          onSave={(values) => save.mutate(values)}
          saving={save.isPending}
        />
      )}
    </div>
  );
}
function TaskModal({
  events,
  onClose,
  onSave,
  saving,
}: {
  events: Awaited<ReturnType<typeof eventRepository.list>>;
  onClose: () => void;
  onSave: (values: TaskForm) => void;
  saving: boolean;
}) {
  const { register, handleSubmit } = useForm<TaskForm>({
    defaultValues: { eventId: "", title: "", dueAt: "", priority: "2" },
  });
  return (
    <Modal title="Nueva tarea" onClose={onClose}>
      <form className="space-y-5" onSubmit={handleSubmit(onSave)}>
        <FieldLabel label="Tarea*">
          <input className="field" {...register("title", { required: true })} />
        </FieldLabel>
        <FieldLabel label="Evento">
          <select className="field" {...register("eventId")}>
            <option value="">General</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.title}
              </option>
            ))}
          </select>
        </FieldLabel>
        <div className="grid gap-5 sm:grid-cols-2">
          <FieldLabel label="Vencimiento">
            <input className="field" type="datetime-local" {...register("dueAt")} />
          </FieldLabel>
          <FieldLabel label="Prioridad">
            <select className="field" {...register("priority")}>
              <option value="1">Alta</option>
              <option value="2">Normal</option>
              <option value="3">Baja</option>
            </select>
          </FieldLabel>
        </div>
        <div className="flex justify-end gap-3 border-t border-border pt-5">
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-primary" disabled={saving}>
            Crear
          </button>
        </div>
      </form>
    </Modal>
  );
}
